import type { ActivityLogElement } from "@octopusdeploy/octopus-server-client";
import { ActivityLogEntryCategory } from "@octopusdeploy/octopus-server-client";
import cn from "classnames";
import * as React from "react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createLinkGeneratorForElement, getSelectedLogLine, toggleSelectedLine } from "~/areas/tasks/components/Task/TaskLog/TaskLogUtil";
import type { UniqueActivityElement } from "~/components/TaskLogLines/TaskLogBlock";
import { TaskLogGap } from "~/components/TaskLogLines/TaskLogGap";
import { TaskLogLine } from "~/components/TaskLogLines/TaskLogLine";
import styles from "~/components/TaskLogLines/style.module.less";
import logStyles from "./taskLogBlock.module.less";
import { TASK_LOG_MARKERS, CATEGORY_PADDING_WIDTH } from "./taskLogConstants";
interface TaskLogLinesProps {
    lines: ActivityLogElement[];
    parent?: UniqueActivityElement;
    showTimestamps: boolean;
    onFetchRange(start: number, end: number): void;
    isFetchDisabled: boolean;
}
const TaskLogLines = (props: TaskLogLinesProps) => {
    const nav = { location: useLocation(), navigate: useNavigate() };
    const selfRef = React.useRef<HTMLDivElement>(null);
    useEffect(() => {
        const currentElement = selfRef.current;
        if (!currentElement) {
            return;
        }
        const shouldProcessSelection = (selection: string): boolean => {
            return selection.includes(TASK_LOG_MARKERS.OCTOCOPY) || selection.includes(TASK_LOG_MARKERS.OCTOLINE) || selection.includes(TASK_LOG_MARKERS.OCTOCATEGORY);
        };
        // ie: "123##OCTOLINE##\nStep 1: Run a script" to "\nStep 1: Run a script"
        const cleanLineNumbers = (line: string): string => {
            return line.split(TASK_LOG_MARKERS.OCTOLINE)[1] ?? line;
        };
        // ie: "Tue, Apr 8th 2025 17:55:24 +10:00\nInfo##OCTOCATEGORY##\nStep 2: Deploy a certificate
        const formatTimestampCategoryLine = (content: string): string => {
            const parts = content
                .split("\n")
                .map((part) => part.trim())
                .filter((part) => part);
            // Tue, Apr 8th 2025 17:55:24 +10:00
            const timestamp = parts[0];
            // Info##OCTOCATEGORY##
            const categoryPart = parts.find((part) => part.includes(TASK_LOG_MARKERS.OCTOCATEGORY));
            // Info
            const category = categoryPart ? categoryPart.replace(TASK_LOG_MARKERS.OCTOCATEGORY, "") : "";
            // Step 2: Deploy a certificate
            const categoryIndex = parts.findIndex((part) => part.includes(TASK_LOG_MARKERS.OCTOCATEGORY));
            const messageLines = parts.slice(categoryIndex + 1);
            const paddedCategory = category.padEnd(CATEGORY_PADDING_WIDTH, " ");
            const timeStampAndCategory = `${timestamp}  ${paddedCategory}`;
            if (messageLines.length === 0) {
                return timeStampAndCategory;
            }
            const firstLine = `${timeStampAndCategory}${messageLines[0]}`;
            if (messageLines.length === 1) {
                return firstLine;
            }
            // Apply indentation on messages with multiple lines. ie:
            //
            // Tue, Apr 8th 2025 17:55:24 +10:00  Verbose     Environment Information:
            //                                                  IsRunningInContainer: False
            //                                                  MinimumThreadPoolCount: 600
            //
            const indentSize = timeStampAndCategory.length + 2; // Two spaces is to retain the existing multi-line formatting.
            const indent = " ".repeat(indentSize);
            const continuationLines = messageLines.slice(1).map((line) => `${indent}${line}`);
            return [firstLine, ...continuationLines].join("\n");
        };
        const formatSimpleLine = (content: string): string => {
            const parts = content.split("\n").filter((part) => part.trim().length > 0);
            return parts.join("\n");
        };
        const processLogLine = (line: string): string => {
            if (!line.trim())
                return "";
            const cleaned = cleanLineNumbers(line);
            if (cleaned.includes(TASK_LOG_MARKERS.OCTOCATEGORY)) {
                return formatTimestampCategoryLine(cleaned);
            }
            else {
                return formatSimpleLine(cleaned);
            }
        };
        const prettyCopyListener = (event: ClipboardEvent) => {
            if (document === null)
                return;
            const selection = document.getSelection()?.toString() ?? "";
            if (!shouldProcessSelection(selection))
                return;
            const logLines = selection.split(TASK_LOG_MARKERS.OCTOCOPY);
            const processedLines = logLines.map(processLogLine).filter((line) => line.length > 0);
            const cleaned = processedLines.join("\n");
            event.clipboardData?.clearData();
            event.clipboardData?.setData("text/plain", cleaned);
            event.preventDefault();
        };
        currentElement.addEventListener("copy", prettyCopyListener);
        return () => {
            currentElement.removeEventListener("copy", prettyCopyListener);
        };
    });
    if (!(props.lines && props.lines.length > 0))
        return <></>;
    const linkTemplate = (props.parent && createLinkGeneratorForElement(nav.location, props.parent.uniqueId)) || ((l: number) => undefined);
    const onToggleLine = (line: ActivityLogElement) => props.parent && toggleSelectedLine(nav, props.parent.uniqueId, line.Number);
    const selection = getSelectedLogLine(nav);
    const lastLine = props.lines[props.lines.length - 1];
    const isBig = (lastLine.Number != undefined && lastLine.Number > 5000) ?? false;
    return (<div ref={selfRef} className={cn(logStyles.lines, styles.copyHelper)} data-testid="task-log-lines">
            {props.lines.map((l, n) => l.Category === ActivityLogEntryCategory.Gap ? (<TaskLogGap key={l.Number || n} line={l} splitGaps={isBig} showTimestamps={props.showTimestamps} onFetchRange={props.onFetchRange} isFetchDisabled={props.isFetchDisabled}/>) : (<TaskLogLine key={l.Number || n} line={l} link={l.Number ? linkTemplate(l.Number) : undefined} onToggleLine={onToggleLine} selected={selection?.line === l.Number && selection?.elementUniqueId == props.parent?.uniqueId} showTimestamps={props.showTimestamps}/>))}
        </div>);
};
export default TaskLogLines;
